import { RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

interface UpdateFloatingCardProps {
  onRefresh: () => void;
  onDismiss: () => void;
}

/**
 * `isVisible` used to be a third prop. It was declared, defaulted to `true`, and
 * never read — both call sites already gate on a conditional, so the component
 * only ever mounts when it should be visible. Removed rather than wired up.
 */
export function UpdateFloatingCard({ onRefresh, onDismiss }: UpdateFloatingCardProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Small delay to ensure the component is mounted before starting animation
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsAnimating(false);
    // Delay the actual dismiss to allow animation
    setTimeout(() => onDismiss(), 400);
  };

  /**
   * The card rests one 1rem gutter clear of the rail's trailing edge.
   *
   * ⚠️ THIS MUST NOT BE A HARDCODED NUMBER, AND IT MUST NOT BRANCH ON
   * `sidebarCollapsed`. It was both: an arbitrary-value 240px expanded / 72px
   * collapsed, from a rail that was a fixed 14rem. NAV-2.4 made the rail size to
   * its longest label (auto width, floored at 14rem, capped at 20rem), so on any tenant
   * whose nav labels push past 224px the card came to rest UNDER the sidebar —
   * and since it deliberately sits at `z-[50]`, behind the rail, "under" means
   * invisible rather than merely overlapping.
   *
   * `--app-sidebar-w` is the rail's own measurement of itself, published by a
   * ResizeObserver in Sidebar.tsx. It already reports `3.5rem` while collapsed,
   * which is what retires the branch: one expression now covers both states, and
   * the collapsed result is the same 72px the old branch hardcoded.
   *
   * The fallback matches the rail's 14rem floor, not its old fixed width, so
   * a render before the first publish under-shoots into the gutter rather than
   * over-shooting under the nav.
   */
  return (
    <div
      className="fixed bottom-4 left-4 md:left-[calc(var(--app-sidebar-w,14rem)_+_1rem)] z-[50] transition-all duration-500 ease-in-out"
      style={{
        transform: isAnimating ? 'translateX(0)' : 'translateX(-100%)',
        opacity: isAnimating ? 1 : 0,
      }}
    >
      <div className="bg-background border border-border rounded-lg shadow-lg p-3 max-w-xs backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1 bg-gradient-to-br from-primary/20 to-accent/20 rounded">
            <Sparkles className="h-3 w-3 text-primary" />
          </div>
          <h4 className="text-xs font-medium text-foreground">
            Update Available
          </h4>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          We've made improvements! Refresh to see what's new.
        </p>

        <div className="flex gap-2">
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="sm"
            className="flex-1 text-xs h-7 hover:bg-accent hover:text-accent-foreground transition-all duration-200 hover:scale-[1.02]"
          >
            Dismiss
          </Button>
          <Button
            onClick={onRefresh}
            size="sm"
            className="flex-1 flex items-center gap-1 text-xs h-7 hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}